import React, { useState } from "react";
import * as BaseAccordion from "@radix-ui/react-accordion";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import MinusIconBasic from "@ecologi/assets/icons/minus-icon.svg";
import PlusIcon from "@ecologi/assets/icons/plus-basic-icon.svg";

export type AccordionItem = {
  heading: string;
  description: string;
};

type AccordionProps = {
  items: AccordionItem[];
  className?: string;
};

const StyledAccordion = styled(BaseAccordion.Root)({
  width: "100%",
});

const StyledItem = styled(BaseAccordion.Item)(({ theme }) => ({
  "&:last-child": {
    borderBottom: `2px solid ${theme.colors.mist}`,
  },
  borderTop: `2px solid ${theme.colors.mist}`,
}));

const StyledTrigger = styled(BaseAccordion.AccordionTrigger)(({ theme }) => ({
  all: "unset",
  fontFamily: "inherit",
  backgroundColor: "transparent",
  height: "54px",
  cursor: "pointer",
  boxSizing: "border-box",
  padding: "6px",
  flex: 1,
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.forest}`,
    outlineOffset: "2px",
    position: "relative",
    zIndex: 1,
    borderColor: "transparent",
  },
  "@media (hover: hover)": {
    "&:hover": {
      backgroundColor: `${theme.colors.mist}`,
    },
  },
}));

const StyledContent = styled(BaseAccordion.Content)({
  marginTop: "4px",
  marginBottom: "40px",
  boxSizing: "border-box",
  padding: "6px",
});

export const Accordion = ({ items, className }: AccordionProps) => {
  const [value, setValue] = useState("item-1");
  return (
    <StyledAccordion
      type="single"
      value={value}
      onValueChange={setValue}
      collapsible
      className={className}
    >
      {items.map((item, index) => (
        <StyledItem key={index} value={`accord-item-${index}`}>
          <BaseAccordion.Header>
            <StyledTrigger>
              <Typography use="body3">{item.heading}</Typography>
              {value === `accord-item-${index}` ? (
                <MinusIconBasic />
              ) : (
                <PlusIcon />
              )}
            </StyledTrigger>
          </BaseAccordion.Header>
          <StyledContent>
            <Typography use="body2">{item.description}</Typography>
          </StyledContent>
        </StyledItem>
      ))}
    </StyledAccordion>
  );
};
